"use client";

import React from "react";
import { Phone, Mail, Clock, MapPin, CircleCheck, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const ContactInfoItem = ({
  icon: Icon,
  title,
  content,
  href
}: {
  icon: any;
  title: string;
  content: string;
  href?: string;
}) => <div data-section-id="22199" className="flex items-start gap-4 group">
    <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/5 shadow-sm backdrop-blur-sm transition-colors group-hover:bg-white/10">
      <Icon className="h-5 w-5 text-secondary" />
    </div>
    <div>
      <h4 className="font-sans text-lg font-bold tracking-tight text-foreground">{title}</h4>
      {href ? <Link to={href} className="text-muted-foreground hover:text-primary transition-colors font-mono text-sm">
          {content}
        </Link> : <p className="text-muted-foreground font-mono text-sm">{content}</p>}
    </div>
  </div>;
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mb-2">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-primary/50 text-primary hover:bg-primary/10" onClick={() => window.location.reload()}>
          Start New Quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background/50 border-white/10 backdrop-blur-sm focus:border-secondary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background/50 border-white/10 backdrop-blur-sm focus:border-secondary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="07700 900000" {...field} className="bg-background/50 border-white/10 backdrop-blur-sm focus:border-secondary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="coverage_type" rules={{
        required: "Please select vehicle make"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Vehicle Make</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-white/10 backdrop-blur-sm focus:ring-secondary">
                    <SelectValue placeholder="Select Make" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-popover border-border">
                  {["Audi / VW Group", "BMW / Mini", "Ford", "Vauxhall", "Mercedes-Benz", "Land Rover / Jaguar", "Other / Commercial"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="language_preference" rules={{
      required: "Please select service type"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-foreground/80">Service Required</FormLabel>
            <FormControl>
              <div className="flex gap-4">
                {["Spare Key", "Lost All Keys", "Lockout"].map((lang, i) => <label key={lang} data-index={i} className={cn("flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-2 transition-all hover:bg-white/5", field.value === lang ? "border-secondary bg-secondary/10 text-secondary ring-1 ring-secondary" : "border-white/10 bg-background/50")}>
                    <input type="radio" className="accent-secondary h-4 w-4" checked={field.value === lang} onChange={() => field.onChange(lang)} />
                    <span className="text-xs font-bold uppercase tracking-wider">{lang}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_info" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Location & Vehicle Details</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[80px] w-full rounded-md border border-white/10 bg-background/50 px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary/50 disabled:cursor-not-allowed disabled:opacity-50 backdrop-blur-sm" placeholder="Where are you located in Norfolk/East Anglia?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-bold uppercase tracking-widest rounded-full shadow-lg shadow-primary/20" disabled={isSubmitting}>
        {isSubmitting ? "Processing..." : "Get My Free Quote"}
      </Button>
      
      <p className="text-center text-[10px] uppercase tracking-widest text-muted-foreground/60 mt-4 font-mono">
        Rapid response across Norfolk & East Anglia. 100% Secure.
      </p>
    </Form>;
}
export default function StartYourProtection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="get-back-on-the-road" className="relative w-full py-20 lg:py-28 overflow-hidden bg-background dark">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
        <div className="absolute bottom-[10%] left-[5%] w-[30%] h-[30%] rounded-full bg-primary/5 blur-[100px]" />
      </div>

      <div ref={ref} className={cn("container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div className="space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs font-bold uppercase tracking-widest text-primary shadow-sm backdrop-blur-md border border-white/10">
                <ShieldCheck className="h-4 w-4 text-secondary" />
                <span>24/7 Rapid Response</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-sans font-extrabold text-foreground leading-tight tracking-tighter">
                Get Back On The Road <br />
                <span className="text-primary italic">Book a Spare Key</span>
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Lost your only key or need a reliable spare? Our mobile locksmiths come to you across Norfolk and East Anglia with dealer-level technology.
              </p>
              
              <p className="text-lg text-secondary max-w-md leading-relaxed italic border-l-4 border-primary/50 pl-4">
                "Mobile service at your doorstep. Professional key programming. Significantly cheaper than main dealers."
              </p>
            </div>

            <Separator className="bg-white/10" />

            <div className="space-y-8">
              <ContactInfoItem icon={Phone} title="Emergency Hotline" content="07700 900000" href="tel:+447700900000" />
              <ContactInfoItem icon={Mail} title="General Enquiries" content="hello@angliacarkeys.co.uk" href="mailto:hello@angliacarkeys.co.uk" />
              <ContactInfoItem icon={Clock} title="Service Availability" content="24 Hours / 7 Days a Week" />
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute inset-0 bg-secondary/5 blur-3xl rounded-full transform translate-y-4 scale-95 -z-10" />
            
            <Card className="border-white/10 bg-card/80 shadow-2xl backdrop-blur-xl overflow-hidden rounded-xl">
              <CardHeader className="bg-white/5 border-b border-white/5 pb-6">
                <CardTitle className="text-2xl font-sans font-bold text-foreground">Request a Quote</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Complete your vehicle details and we'll provide a price instantly.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                <FormProvider formId="quote_form_home" sectionName="get-back-on-the-road" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                coverage_type: "",
                language_preference: "Spare Key",
                additional_info: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                coverage_type: {
                  required: true
                },
                language_preference: {
                  required: true
                }
              }} submitText="Get My Free Quote" successMessage="Thank you! Your quote request has been received. We will contact you shortly.">
                  <QuoteForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}